import { Button } from '../../catalyst/button';
import backend from '../../services/backend';
import { useToast } from '../Toast';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import clsx from 'clsx';

export default function RecoverStrandedButton({
  documentId,
  count,
}: {
  documentId: string;
  count: number;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  if (count <= 0) {
    return null;
  }

  const Icon = loading ? ArrowPathIcon : WrenchScrewdriverIcon;

  return (
    <Button
      className="bg-amber-500 text-white hover:bg-amber-600 md:py-0 flex items-center gap-2"
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        backend
          .recoverDocument(documentId)
          .then(({ data }) => {
            queryClient.invalidateQueries({ queryKey: ['document', documentId] });
            queryClient.invalidateQueries({ queryKey: ['documentPages', documentId] });
            queryClient.invalidateQueries({ queryKey: ['stranded', documentId] });

            // The full-cascade branch runs in the background (mode 'async'); the
            // stranded poller reflects progress from here, so just confirm start.
            if (data.mode === 'async') {
              toast.info(
                data.scopeConflict
                  ? 'Another recovery is already in progress with a different recovery scope'
                  : data.alreadyRunning
                    ? 'Recovery already in progress'
                    : 'Recovery started',
              );
            } else if (data.recovered > 0) {
              toast.success('Recovery started');
            }
          })
          .catch(() => toast.error('Failed to start recovery'))
          .finally(() => {
            setLoading(false);
          });
      }}
    >
      <Icon className={clsx('h-4 w-4', loading && 'animate-spin')} />
      {loading ? 'Recovering…' : `Recover ${count} stranded page${count === 1 ? '' : 's'}`}
    </Button>
  );
}
